// ============================================================================
// toastStore.ts — ГЛОБАЛЬНАЯ система оповещений (SSoT/SRP)
// Единый store: тосты (всплывающие окна) + спецраздел «Уведомления» (важные).
// - duration > 0 — исчезают сами; duration = 0 — висят, пока не закроют;
// - priority = true — добавляются в спецраздел (история уведомлений).
// ============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastLink {
  label: string;
  href: string;
}

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastDetails {
  text?: string;
  link?: ToastLink;
  /** Кнопка-действие в тосте (например «Перезагрузить» для обновления) */
  action?: ToastAction;
  text2?: string;
}

export interface Toast {
  id: number;
  type: ToastType;
  message: string;
  details?: ToastDetails;
  /** Мс; 0 = persistent (висят, пока не закроют) */
  duration: number;
  /** Важные уведомления — попадают в спецраздел (история) */
  priority: boolean;
  createdAt: number;
}

export interface ToastOptions {
  duration?: number;
  details?: ToastDetails;
  priority?: boolean;
  /** Только история (колокольчик), без всплывающего тоста: карточку
   *  показывает единый NotificationPopupHost. */
  silent?: boolean;
}

interface ToastStore {
  toasts: Toast[];
  /** Спецраздел: важные уведомления (история) */
  notifications: Toast[];
  showToast: (message: string, type?: ToastType, options?: ToastOptions) => void;
  showSuccess: (message: string, options?: ToastOptions) => void;
  showError: (message: string, options?: ToastOptions) => void;
  showWarning: (message: string, options?: ToastOptions) => void;
  showInfo: (message: string, options?: ToastOptions) => void;
  dismiss: (id: number) => void;
  dismissNotification: (id: number) => void;
  clearNotifications: () => void;
}

let nextId = 0;

export const useToastStore = create<ToastStore>()(
  persist(
    (set) => ({
      toasts: [],
      notifications: [],

      showToast: (message, type = 'info', options) => {
        const id = nextId++;
        const duration = options?.duration ?? (type === 'error' ? 0 : type === 'warning' ? 6000 : 4000);
        const toast: Toast = {
          id,
          type,
          message,
          details: options?.details,
          duration,
          priority: !!options?.priority,
          createdAt: Date.now(),
        };
        set((state) => ({
          // silent — только история: видимую карточку рисует NotificationPopupHost.
          toasts: options?.silent ? state.toasts : [...state.toasts, toast],
          // Важные — в спецраздел (история уведомлений)
          notifications: toast.priority ? [toast, ...state.notifications] : state.notifications,
        }));
        if (duration > 0 && !options?.silent) {
          setTimeout(() => {
            set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
          }, duration);
        }
      },

      showSuccess: (message, options) => {
        useToastStore.getState().showToast(message, 'success', options);
      },

      showError: (message, options) => {
        useToastStore.getState().showToast(message, 'error', options);
      },

      showWarning: (message, options) => {
        useToastStore.getState().showToast(message, 'warning', options);
      },

      showInfo: (message, options) => {
        useToastStore.getState().showToast(message, 'info', options);
      },

      dismiss: (id) => {
        set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
      },

      dismissNotification: (id) => {
        set((state) => ({ notifications: state.notifications.filter((n) => n.id !== id) }));
      },

      clearNotifications: () => {
        set({ notifications: [] });
      },
    }),
    {
      // ВАЖНЫЕ уведомления (колокольчик) переживают перезагрузку/рефреш PWA:
      // уведомление о новой версии могло прийти на странице входа (колокольчика
      // нет) — persist сохраняет его, и после входа оно появится в колокольчике.
      name: 'hydra_notifications',
      partialize: (s) => ({
        notifications: s.notifications.map((n) => ({
          ...n,
          // Функции (details.action.onClick) не сериализуются — убираем.
          // «Перезагрузить» восстанавливается в NotificationsBell по label.
          details: n.details
            ? { ...n.details, action: n.details.action ? { label: n.details.action.label } : undefined }
            : undefined,
        })),
      }),
    },
  ),
);
